import { create } from "zustand";

import { formatRetention } from "../../lib/const.js";
import { t } from "../contract/strings";
import type { Credentials, Template } from "../../lib/store.js";
import type {
  AccountOut,
  PublicConfigPayload,
  SessionOut,
  SessionResultOut,
  SessionResultPendingOut,
} from "../contract/api";
import type { ConfigAvailability } from "../engine/server-config";
import type { OtherMeeting, OtherMeetingAction } from "../engine/other-meeting";
import type { SignInOutcome } from "../engine/signin";
import type { StatusKind } from "../ui";
import { isPlainLaunch, readWelcomeDone, writeWelcomeDone } from "./welcome";

/**
 * What `/v1/accounts/me` says about the person signed in: the server's `AccountOut`.
 *
 * `account_id` is the canonical id `recordingIdentityBlock` compares against, and
 * `participant_id` is what future merges resolve this account's devices to.
 */
export type Account = AccountOut;

/**
 * Where the session engine has got to. The route navigates on changes to this.
 *
 * `processing` is "record now, read later": this device's wrap-up is finished and the
 * server is working on the meeting. Nothing on the device is at risk any more, so the
 * person may leave; the result arrives by email, and `done` if they are still here.
 */
export type SessionPhase =
  | "idle"
  | "recording"
  | "wrapping"
  | "processing"
  | "done"
  | "left"
  | "revoked";

/**
 * The pending 202's `processing_stage` (`protocol.ProcessingStage`), taken from the
 * generated wire type so it cannot drift from the server's enum. A closed vocabulary:
 * anything else the server sends reads as unknown (`processingStageOf`).
 */
export type ProcessingStage = NonNullable<SessionResultPendingOut["processing_stage"]>;

const PROCESSING_STAGES: readonly ProcessingStage[] = [
  "aligning",
  "finding_speakers",
  "transcribing",
  "summarising",
];

/** A wire value as a stage, or `null` when absent or not one this client knows. */
export function processingStageOf(value: unknown): ProcessingStage | null {
  return typeof value === "string" && (PROCESSING_STAGES as readonly string[]).includes(value)
    ? (value as ProcessingStage)
    : null;
}

/** The plain status every processing stage reads as, wherever a session is shown. */
export const PROCESSING_LABEL = t("state.processing_label");

/**
 * What a person reads for each stage: the same plain status for all of them. The stages
 * are pipeline internals; what matters to the reader is that their notes are on the way.
 */
export const PROCESSING_STAGE_LABEL: Record<ProcessingStage, string> = {
  aligning: PROCESSING_LABEL,
  finding_speakers: PROCESSING_LABEL,
  transcribing: PROCESSING_LABEL,
  summarising: PROCESSING_LABEL,
};

/**
 * Whether a published result is still waiting for its summary.
 *
 * `summary_status` supersedes `summary_pending` on new results (#355); older ones carry
 * only the boolean. A `failed` summary is final -- nothing more is coming -- so it is
 * not pending, whatever the older field says.
 */
export function summaryPendingOf(result: { summary_status?: unknown; summary_pending?: unknown }): boolean {
  if (result.summary_status === "pending") return true;
  if (result.summary_status === "done" || result.summary_status === "failed") return false;
  return result.summary_pending === true;
}

/**
 * This device's upload against its capture, in samples.
 *
 * `acked` is the server's `next_sample_offset`, not what the socket has sent: sent
 * bytes can still be sitting in a buffer that a dropped connection throws away, and
 * "all audio uploaded" must mean the server has it.
 */
export interface UplinkHealth {
  captured: number;
  acked: number;
  sampleRate: number;
}

/** Upload health in the words the recording screen uses. */
export type UploadHealth = "uploaded" | "catching_up" | "reconnecting" | "refused";

/** How far behind the capture the acknowledged upload may be and still read as caught up. */
export const UPLOAD_CAUGHT_UP_SEC = 2;

/**
 * Upload health from the uplink state and the last cursor report.
 *
 * Anything but a live socket reads as `reconnecting` while recording: after #356 a
 * dropped uplink keeps retrying, and a device that has not connected yet is in the
 * same position -- its audio is held locally until the socket is up.
 *
 * `refused` is the one exception, and it wins over everything: the server said no to
 * this recording's audio and nothing will retry, so "reconnecting" would promise an
 * upload that is not coming (#410).
 */
export function uploadHealthOf(
  uplinkState: "off" | "starting" | "live" | "ended",
  health: UplinkHealth | null,
  refused = false
): UploadHealth {
  if (refused) return "refused";
  if (uplinkState !== "live") return "reconnecting";
  if (!health) return "catching_up";
  const behindSec = Math.max(0, health.captured - health.acked) / Math.max(1, health.sampleRate);
  return behindSec <= UPLOAD_CAUGHT_UP_SEC ? "uploaded" : "catching_up";
}

/**
 * What the server returns from create or join, as the engine holds it.
 *
 * A partial view of the generated `SessionOut`: `session_id` is always present, the rest
 * is read defensively because a response that omits a field must read as unknown.
 */
export type SessionHandle = Pick<SessionOut, "session_id"> & Partial<SessionOut>;

/**
 * Application state that outlives a route.
 *
 * Zustand rather than context because the session engine writes to this from its own loop
 * -- a scoring tick, a claim leaving the outbox, a level reading -- and a context turns
 * each of those into a re-render cascade through every provider consumer. The session
 * slice joins in P3; what is here is what boot establishes.
 */

/**
 * Where the voice model is (#658, #659). Boot no longer waits for it: Home warms it in the
 * background, and the screens that need it wait on `ensureModelReady` and render this.
 *
 * - `fraction` is the download's progress, or null when the response carries no length.
 * - `download` is whether this load has to fetch the model rather than read a saved copy,
 *   which is when the screen adds the one-time size note.
 * - `failure` says which sentence a failed load shows: `offline` is no saved copy and no
 *   connection, `failed` is everything else.
 */
export interface ModelReadiness {
  status: "idle" | "loading" | "ready" | "failed";
  fraction: number | null;
  download: boolean;
  failure: "offline" | "failed" | null;
}

export interface AppState {
  /** Boot is finished and its overlay is down. The voice model is not part of boot any more. */
  ready: boolean;

  /** The voice model's readiness. See `ModelReadiness` and `engine/model-readiness.ts`. */
  model: ModelReadiness;
  /**
   * Which action is waiting on the voice model, so its screen shows "Getting ready…" (or
   * the error with Try again) in place of that action's button: Start (`create`), Join
   * (`join`) or a voice-setup take (`take`). Store state rather than a screen's own, so the
   * parity gallery can seed it. Cleared by the screen that set it, including on leaving.
   */
  modelWait: "create" | "join" | "take" | null;
  /**
   * A start or join refused because another meeting is still live (#735), with Home's End
   * it or Leave it beside it: the meeting, which of the two is in flight, and whether that
   * wait has passed the point where it says it may take minutes. Store state so the parity
   * gallery can seed it, like `modelWait`. Home clears it when it unmounts.
   */
  otherMeeting: {
    meeting: OtherMeeting;
    busy: OtherMeetingAction | null;
    slow: boolean;
  } | null;

  /**
   * A start or join the server refused with `ai_consent_required` (#915): "How MeetFolks
   * uses AI" shows whatever `features.ai_consent` said at boot, then `retry` runs once the
   * person agrees. `version` is the refusal's, for when the account cannot be read again;
   * `chirp` says the retry is a start, whose chirp needs a context made in the Agree click.
   * Store state so the sheet, which sits above every route, can serve Home's refusal.
   */
  aiConsentDemand: {
    version: number | null;
    chirp: boolean;
    retry: ((chirpCtx: AudioContext | null) => void) | null;
  } | null;

  /**
   * `/v1/config`, or null when this deployment could not be asked.
   *
   * Kept raw as well as rendered because different screens read different fields of it --
   * the Create button asks whether accounts exist at all, the disclosure asks what happens
   * to audio.
   */
  serverConfig: PublicConfigPayload | null;
  /**
   * Why `serverConfig` is null, when it is -- and never left for a reader to guess.
   *
   * `null` alone cannot tell "still asking" from "asked four times and gave up", and the
   * two read differently to the user (#124). An explicit `accepts_audio: false` is a third
   * thing again: the deployment takes no audio and this device will not record (#388). So boot writes a state
   * beside the body, `"pending"` while `retryConfig` is still going and `"unavailable"`
   * when it has finished failing, and the screens that would otherwise say nothing have
   * something definite to say. See `server-config.ts`.
   */
  serverConfigState: ConfigAvailability;
  /**
   * What this deployment actually does with audio and transcripts, in a sentence.
   *
   * Composed at boot rather than written into the markup so it reports *this* server. The
   * placeholder below is what shows when `/v1/config` cannot be reached: the one claim
   * that is true of every deployment because it is a property of the client.
   */
  disclosure: string;

  /**
   * This device's credential. The *account* credential is deliberately absent: it is an
   * HttpOnly cookie the page cannot read, which is the point of using one.
   */
  credentials: Credentials | null;
  /**
   * The signed-in account, or null. Optional by design -- recording, joining and reading a
   * transcript all run on the device token, so most of this app never consults it. What is
   * held here is the server's *description* of the account (email, created_at); the
   * credential itself is the cookie and is never in JS state.
   */
  account: Account | null;
  /**
   * Whether this device's account link agrees with the account signed in on it (#38).
   *
   * The signed claim identity is the *device*; the cookie only says who is signed in.
   * When they disagree, one account resolves every one of their devices onto a single
   * participant, so two people collapse into one speaker with coverage still reading
   * healthy. This is the state that makes that relationship visible to the UI and the
   * engine, and `recordingIdentityBlock` is the gate over it.
   *
   * Recomputed, never persisted: it is a property of the current cookie + device token
   * pair, not of this device, and a stale IndexedDB copy could unblock a real conflict.
   */
  deviceAccountLink: DeviceAccountLinkState;
  /**
   * Whether boot has finished reading storage. The route guards must not act before it: on
   * a signed-in, enrolled device the first frame would otherwise redirect to the front door
   * and back, and `history` would keep both.
   */
  identityLoaded: boolean;
  /**
   * Whether boot has an answer about the account -- including "nobody".
   *
   * Separate from `identityLoaded` because they arrive at different times: credentials come
   * from IndexedDB in milliseconds, the account from a request that is deliberately run
   * *inside* the model-download window. [V] A guard that waited only for the first sent
   * `/account` to the front door and straight on to home, because at the instant it ran the
   * account was still null and both redirects were legitimate given what was known.
   */
  accountLoaded: boolean;
  /**
   * Whether this device is past the first-run Welcome (#1095): Get started was tapped, or
   * the device has been signed in or set up here (`state/welcome.ts`).
   */
  welcomeDone: boolean;
  /**
   * Whether this launch was a plain open of `/`, read once as the page loads, before boot
   * clears a magic link's fragment. Only a plain launch is ever shown the Welcome; an entry
   * link goes where it goes.
   */
  launchPlain: boolean;
  /**
   * The session this device is in, once created or joined. Held outside the router because
   * the join code is a secret and has no business in a URL. P3's engine owns the recorder
   * that goes with it.
   */
  session: SessionHandle | null;
  /**
   * Where the session engine is, so the route can navigate on it.
   *
   * The engine runs outside React and cannot navigate; it moves this and the screen
   * reacts. `recording` is separate from the phase because the microphone is released at
   * the *start* of wrap-up, and the navigation blocker asks about the microphone.
   */
  phase: SessionPhase;
  recording: boolean;
  /**
   * Between Start or Join and this device's first captured audio (#658): the session
   * exists and the microphone is opening, but nothing is recorded yet, so the screen says
   * "Setting up the meeting…" and the clock does not run. A reload here would orphan the
   * session, so the update flow waits on it as it waits on `recording`.
   */
  settingUp: boolean;
  /**
   * Whether wrap-up is running: set when `finishSession` takes the stopping latch, cleared
   * in the same `finally` that releases it.
   *
   * [V] `recording` alone is not the busy window and treating it as one cost two things.
   * The microphone is released at the *start* of wrap-up, so `recording` goes false with
   * ten to thirty seconds of work still to do -- final scoring pass, claim drain, tail
   * audio upload, finalise -- and the update flow, which reloads when recording *ends*,
   * fired 1.2 s into that. The reload takes the un-posted outbox and the tail audio with
   * it, and the session is never finalised for anybody at the table. `beforeunload` never
   * had the bug because it guards on the recorder reference, which survives until
   * teardown; this flag is that same window, in the store, where React and the update flow
   * can both read it.
   *
   * It is also what re-enables Stop. Gating the button on `phase === "wrapping"` left it
   * dead forever when wrap-up threw, next to an error saying "Press again".
   */
  stopping: boolean;
  /**
   * Whether this device has told the server it is done, which turns Stop into "Get the
   * summary".
   *
   * In the store rather than read from the engine at render time: `hasReported()` returned
   * a plain boolean from module state, so React had no way to know it had changed and the
   * label only corrected itself when some other field happened to re-render the screen.
   */
  reported: boolean;
  /** Live readings, written from the 500 ms tick. The level meter is not one of these. */
  elapsedSec: number;
  claimedSec: number;
  windows: number;
  isCreator: boolean;
  chirping: boolean;
  /**
   * The latest capture stall this device reported, while it is still worth
   * showing. A transient toast is invisible to a participant who was looking
   * at another app when the stall happened -- which is exactly when stalls
   * happen -- so the recording screen keeps the most recent one until stop,
   * a fresh recording, or an explicit clear. Cleared alongside `recording`,
   * never carried across sessions.
   */
  capturePause: { gapSec: number; atMs: number } | null;
  /** The wrap-up card: which stage, and how far through if that is knowable. */
  wrap: { key: string; fraction: number | null } | null;
  /** The finished result, and which session it belongs to. */
  result: SessionResultOut | null;
  resultSessionId: string | null;
  /**
   * Where the server is with this session after Stop: the pending 202's
   * `processing_stage`, or `null` when it has not said (before processing starts,
   * between stages it has no name for, or an older server). Unknown, never a stage.
   */
  processingStage: ProcessingStage | null;
  /**
   * This device's upload, as the uplink last reported it: how much audio the recorder
   * holds against how much the server has acknowledged. `null` before the first
   * report of a recording. The recording screen turns it into "all audio uploaded",
   * "catching up" or "reconnecting" (`uploadHealthOf`).
   */
  uplinkHealth: UplinkHealth | null;
  /**
   * The server refused this recording's audio and the device will not retry (#410). Only a
   * typed refusal sets it -- never the per-connection cap or a drop, which reconnect.
   */
  uplinkRefused: boolean;
  /**
   * Where this device's realtime stream is up to, as the *engine* sees it.
   *
   * In the store rather than in the diagnostics component because the stream is
   * owned by the engine and outlives any screen. [V] It used to be component
   * state, which is the same reason the uplink itself used to die on unmount:
   * leaving the panel destroyed both the socket and every trace that it had
   * existed. A screen that can be navigated away from cannot be where the answer
   * to "is this device streaming?" lives.
   *
   * ``ended`` is terminal and deliberate: there is no reconnect (see
   * ``stream-session.ts``), so a socket that dies stays dead for the rest of the
   * session and the view has to say so rather than implying it will come back.
   */
  streamUplinkState: "off" | "starting" | "live" | "ended";
  /**
   * Participant id to display name, for the session this device is in: the devices
   * the recording screen lists. From the create/join response and every state poll.
   */
  roster: Record<string, string>;
  /**
   * This device's token was rejected mid-session (401 -- revoked from another device).
   *
   * A flag rather than clearing the credentials, because the stored ones are deliberately
   * left alone: the template is still this person's voice, and re-registering mints a fresh
   * device id and keypair anyway, so nothing has to be destroyed first. The guards read it
   * as "not enrolled" so the app lands on setup, and a reload starts from storage as before.
   */
  deviceRevoked: boolean;
  /**
   * How a sign-in went, waiting to be shown.
   *
   * A one-shot handoff, because the two things that produce it -- boot, and a link tapped
   * onto an already-open tab -- both live outside React and neither can navigate. The shell
   * reads it, routes on it, and clears it.
   */
  signInOutcome: SignInOutcome | null;
  /**
   * The one status line, addressed to the screen it belongs on.
   *
   * `app.js` had a status element per screen and wrote into whichever one was up, including
   * across a screen change -- enrolment finishing writes a link warning onto *home*, a
   * sign-in outcome writes onto the front door. Carrying the path with the message keeps
   * that and gets two things free: a message can be left for a screen that has not mounted
   * yet, and one left over from a screen somebody has moved on from never shows, because
   * the path no longer matches.
   *
   * Not React Router navigation state, which was tried first and is wrong twice: it is
   * serialised into `history.state`, so a reload re-shows a warning about something that
   * happened once; and [V] a navigation to the path already showing does not remount, so
   * the message never arrived at all -- which is exactly the sign-in case, where the
   * outcome of a link tapped on the front door lands on the front door.
   */
  screenStatus: { path: string; message: string; kind: StatusKind } | null;
  /** The voice template. On this device only, and not recoverable from the server. */
  template: Template | null;
  keypair: CryptoKeyPair | null;

  /**
   * Uncaught errors and rejections, newest last.
   *
   * There is no console on a phone. An exception that only reaches `console.error` is an
   * app that stopped working for no stated reason, and the person holding it is at a table
   * with four others waiting. So every one of them is put on screen.
   */
  faults: string[];

  setReady: (ready: boolean) => void;
  setAccount: (account: Account | null) => void;
  setDeviceAccountLink: (link: DeviceAccountLinkState) => void;
  setAccountLoaded: (loaded: boolean) => void;
  /** Past the Welcome for good: here, and in `localStorage` where it can be kept. */
  finishWelcome: () => void;
  setSession: (session: SessionHandle | null) => void;
  /** One setter for the engine, so a tick is one store write rather than six. */
  setSessionState: (patch: Partial<AppState>) => void;
  setSignInOutcome: (outcome: SignInOutcome | null) => void;
  setScreenStatus: (status: AppState["screenStatus"]) => void;
  setServerConfig: (
    config: PublicConfigPayload | null,
    disclosure: string,
    state: ConfigAvailability
  ) => void;
  setIdentity: (identity: {
    credentials: Credentials | null;
    template: Template | null;
    keypair: CryptoKeyPair | null;
  }) => void;
  reportFault: (what: string, message: string) => void;
  clearFaults: () => void;
}

export const DEFAULT_DISCLOSURE = t("state.default_disclosure");

/** Repeats are dropped rather than stacked: one bad frame can fire this 60 times a second. */
const FAULT_LIMIT = 3;

/**
 * Enrolled means all three: a device credential, a voice template, and the signing key
 * that ties claims to it. Any one missing and this device cannot make an attributable
 * claim, so the guard treats it as not set up rather than as partly set up.
 */
/** The four fields enrolment is decided by, so a caller need not build a whole store. */
export type EnrolmentState = Pick<AppState, "credentials" | "template" | "keypair" | "deviceRevoked">;

export function isEnrolled(state: EnrolmentState): boolean {
  if (state.deviceRevoked) return false;
  return Boolean(state.credentials && state.template && state.keypair);
}

/**
 * Whether this device's account link has been reconciled with the account signed in (#38).
 *
 * Deliberately not part of `isEnrolled`: enrolment answers "can this device make owner
 * claims", this answers "does the signed-in account agree with the owner that already
 * is". Keeping them apart is what lets a signed-out device keep recording and joining:
 * its device stays linked server-side even though the cookie is gone.
 */
export type DeviceAccountLinkState =
  | { status: "checking" }
  | { status: "not_applicable"; reason: "no_device" | "signed_out" }
  | { status: "linked"; accountId: string }
  // Only left behind by a conflict straight after enrolment, which cannot happen for a
  // freshly minted device. At sign-in and boot a conflict resets the device instead (#336).
  | { status: "conflict" }
  | { status: "error"; message: string };

/**
 * Why this device must not start or join a session, or `null` when it may.
 *
 * Fail-closed on `checking`/`conflict`/`error`: a sign-in that installed a cookie
 * before the device link could be established must not record, because the alternative
 * is a single-speaker transcript that looks healthy. `not_applicable` is the way past
 * it: an un-enrolled or signed-out device.
 *
 * Pure, so Home, the account screen and the engine backstop all answer the same way
 * from the same state rather than each re-deriving it.
 */
export function recordingIdentityBlock(
  state: Pick<AppState, "credentials" | "template" | "keypair" | "deviceRevoked" | "account" | "deviceAccountLink">
): "checking" | "conflict" | "error" | null {
  if (!isEnrolled(state)) return null;
  const link = state.deviceAccountLink;
  // Checked *before* the account: these three say "the relationship is unknown or wrong",
  // and an unavailable account body must not be read as "signed out, therefore fine" --
  // that was the hole where a network failure authorised recording (#38).
  if (link.status === "error") return "error";
  if (link.status === "checking") return "checking";
  if (link.status === "conflict") return "conflict";
  if (!state.account) return null; // signed out: the device stays linked server-side, so it keeps working
  if (link.status === "linked" && state.account.account_id && state.account.account_id !== link.accountId) {
    // The server would have answered 409, so this is defensive: a stale `linked` state
    // that a later account switch invalidated must not silently allow recording.
    return "conflict";
  }
  return null;
}

export const useApp = create<AppState>((set) => ({
  ready: false,
  model: { status: "idle", fraction: null, download: false, failure: null },
  modelWait: null,
  otherMeeting: null,
  aiConsentDemand: null,
  serverConfig: null,
  serverConfigState: "pending",
  disclosure: DEFAULT_DISCLOSURE,
  credentials: null,
  account: null,
  // Nothing is known before boot has looked; `not_applicable` keeps a signed-out or
  // un-enrolled device unblocked until `refreshAccount`/sign-in answers.
  deviceAccountLink: { status: "not_applicable", reason: "signed_out" },
  identityLoaded: false,
  accountLoaded: false,
  welcomeDone: readWelcomeDone(),
  launchPlain: typeof location === "undefined" ? false : isPlainLaunch(location),
  session: null,
  phase: "idle",
  recording: false,
  settingUp: false,
  stopping: false,
  reported: false,
  elapsedSec: 0,
  claimedSec: 0,
  windows: 0,
  isCreator: false,
  chirping: false,
  capturePause: null,
  wrap: null,
  result: null,
  resultSessionId: null,
  processingStage: null,
  uplinkHealth: null,
  uplinkRefused: false,
  streamUplinkState: "off",
  roster: {},
  deviceRevoked: false,
  signInOutcome: null,
  screenStatus: null,
  template: null,
  keypair: null,
  faults: [],

  setReady: (ready) => set({ ready }),
  // Any answer is an answer, including null: this is what unblocks the route guards.
  setAccount: (account) => set({ account, accountLoaded: true }),
  setDeviceAccountLink: (deviceAccountLink) => set({ deviceAccountLink }),
  setSession: (session) => set({ session }),
  setSessionState: (patch) => set(patch),
  setAccountLoaded: (accountLoaded) => set({ accountLoaded }),
  finishWelcome: () => {
    writeWelcomeDone();
    set({ welcomeDone: true });
  },
  setSignInOutcome: (signInOutcome) => set({ signInOutcome }),
  setScreenStatus: (screenStatus) => set({ screenStatus }),
  // The state travels with the body because they must never disagree: a `serverConfig`
  // applied late by the retry with a stale `"unavailable"` beside it would leave a warning
  // on screen about audio this device is now uploading fine.
  setServerConfig: (serverConfig, disclosure, serverConfigState) =>
    set({ serverConfig, disclosure, serverConfigState }),
  /*
   * Clears `deviceRevoked`, and that is the whole of the fix.
   *
   * [V] Revocation is sticky by design -- `isEnrolled` returns false while it is set, which
   * is what sends a revoked device back to `/enroll` -- but nothing ever unset it. So
   * re-enrolment succeeded, wrote a new keypair, template and credential, registered a new
   * device row on the server, called this, and `isEnrolled` still said false: straight back
   * to `/enroll`, forever, minting one more device row on every pass. The one moment a
   * device is provably not revoked is the moment it presents a fresh identity, which is
   * exactly here.
   */
  setIdentity: (identity) => set({ ...identity, identityLoaded: true, deviceRevoked: false }),
  reportFault: (what, message) =>
    set((state) => {
      const line = t("state.fault", { what, message });
      if (state.faults.includes(line)) return state;
      return { faults: [...state.faults, line].slice(-FAULT_LIMIT) };
    }),
  clearFaults: () => set({ faults: [] }),
}));

/**
 * The disclosure line, from what the server says it does.
 *
 * `audio_leaves_deployment` is reported separately from `accepts_audio` because they are
 * different questions: somebody told only "this server accepts audio" cannot tell an
 * operator who holds their voice from one who forwards it to a company they have never
 * heard of. This line is read *before* agreeing to be recorded, so stating only the first
 * half -- the flattering half -- is the one thing it must not do.
 */
export function disclosureFor(config: Record<string, unknown> | null): string {
  if (!config) return DEFAULT_DISCLOSURE;
  const bits: string[] = [];
  bits.push(
    config.accepts_audio
      ? // Formatted, not interpolated raw: `/v1/config` reports hours, and "deleted
        // after 720h" asks somebody to do arithmetic on a 30-day window before they
        // agree to be recorded. `formatRetention` is the browser's copy of
        // `constants.format_retention`, kept in step by `tests/test_constants.py`.
        t("state.audio_upload", {
          window: formatRetention(Number(config.audio_retention_hours)),
        })
      : t("state.no_audio")
  );
  if (config.audio_leaves_deployment) bits.push(t("state.third_party_transcribes"));
  if (config.summaries_send_transcript_to_third_party) {
    bits.push(t("state.third_party_summary"));
  }
  return t("state.disclosure", { bits: bits.join("; ") });
}
